@file:Suppress("MagicNumber", "TooManyFunctions")

package com.nonopn.skydome.common.app.pages.promocodes

// Pure logic of the promotion codes (admin list / form and user field). No UI, no network, no serialization:
// kept free of any dependency so that it can be unit tested on its own.

const val PROMO_CODE_MIN_LENGTH = 3
const val PROMO_CODE_MAX_LENGTH = 32
const val PROMO_PERCENT_MIN = 1
const val PROMO_PERCENT_MAX = 100
const val PROMO_FREE_MONTHS_MIN = 1
const val PROMO_FREE_MONTHS_MAX = 24
const val PROMO_COMMISSION_MIN = 1
const val PROMO_COMMISSION_MAX = 100
private const val SECONDS_PER_DAY = 86_400L

private val PromoCodeRegex = Regex("^[A-Z0-9_-]{$PROMO_CODE_MIN_LENGTH,$PROMO_CODE_MAX_LENGTH}$")
private val EmailRegex = Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")
private val WhitespaceRegex = Regex("\\s+")

// ─── Code text ───────────────────────────────────────────────────────────────

/** What is sent for a typed code: spaces removed, upper case (the server is case insensitive anyway). */
fun normalizePromoCode(raw: String): String = raw.replace(WhitespaceRegex, "").uppercase()

/** 3 to 32 letters, digits, - or _ (no space, no accent), once normalized. */
fun isValidPromoCodeText(code: String): Boolean = PromoCodeRegex.matches(normalizePromoCode(code))

/** True when an existing code is renamed: its previous text stops working once saved. */
fun isPromoCodeRenamed(originalCode: String?, newCode: String): Boolean =
    originalCode != null && normalizePromoCode(originalCode) != normalizePromoCode(newCode)

// ─── State ───────────────────────────────────────────────────────────────────

enum class PromoCodeState {
    Active,
    Disabled,
    Expired,
    Exhausted;

    companion object {
        fun parse(raw: String?): PromoCodeState? = entries.firstOrNull { it.name.equals(raw?.trim(), true) }
    }
}

/**
 * State of a code: the one computed by the server; for an unknown or missing value, the same rules locally
 * (disabled first, then expired, then exhausted).
 */
fun resolvePromoCodeState(
    serverState: String?,
    active: Boolean,
    validUntilSecondsUtc: Long?,
    redemptions: Int,
    maxRedemptions: Int?,
    nowSeconds: Long,
): PromoCodeState {
    PromoCodeState.parse(serverState)?.let { return it }
    return when {
        !active -> PromoCodeState.Disabled
        validUntilSecondsUtc != null && validUntilSecondsUtc <= nowSeconds -> PromoCodeState.Expired
        maxRedemptions != null && redemptions >= maxRedemptions -> PromoCodeState.Exhausted
        else -> PromoCodeState.Active
    }
}

// ─── Benefit ─────────────────────────────────────────────────────────────────

/** What a code gives, used for every label ("−20 % on every invoice", "3 free months", "Free"...). */
sealed interface PromoBenefit {
    data class Percent(val percent: Int) : PromoBenefit
    data class FreeMonths(val months: Int) : PromoBenefit
    data class FreeMonthsThenPercent(val months: Int, val percent: Int) : PromoBenefit
    data object Free : PromoBenefit
    data object None : PromoBenefit
}

fun promoBenefit(percentOff: Int?, freeMonths: Int?): PromoBenefit {
    val percent = percentOff?.takeIf { it > 0 }
    val months = freeMonths?.takeIf { it > 0 }
    return when {
        months != null && percent != null -> PromoBenefit.FreeMonthsThenPercent(months, percent)
        months != null -> PromoBenefit.FreeMonths(months)
        percent != null && percent >= PROMO_PERCENT_MAX -> PromoBenefit.Free
        percent != null -> PromoBenefit.Percent(percent)
        else -> PromoBenefit.None
    }
}

// ─── Row actions (admin list) ────────────────────────────────────────────────

enum class PromoRowAction { Disable, Enable, Delete }

/** How the deletion is confirmed: simple for an unused code, "disable instead" first for a used and active one. */
enum class PromoDeleteConfirmation { Simple, SuggestDisable, UsedAlreadyDisabled }

data class PromoRowActions(val primary: PromoRowAction, val secondary: List<PromoRowAction>)

/**
 * Actions of a code (decision of 2026-11-02): a disabled code is re-enabled (POST active true); an active code
 * is disabled (POST active false, reversible), which is the main action, and above all once it has been used.
 * The deletion (irreversible) is always a secondary action with a confirmation.
 */
fun promoRowActions(active: Boolean): PromoRowActions =
    if (active) {
        PromoRowActions(PromoRowAction.Disable, listOf(PromoRowAction.Delete))
    } else {
        PromoRowActions(PromoRowAction.Enable, listOf(PromoRowAction.Delete))
    }

fun promoDeleteConfirmation(active: Boolean, redemptions: Int): PromoDeleteConfirmation = when {
    redemptions <= 0 -> PromoDeleteConfirmation.Simple
    active -> PromoDeleteConfirmation.SuggestDisable
    else -> PromoDeleteConfirmation.UsedAlreadyDisabled
}

// ─── Search / filter (admin list) ────────────────────────────────────────────

enum class PromoStateFilter { All, Active, Disabled, Expired, Exhausted }

fun PromoStateFilter.accepts(state: PromoCodeState): Boolean = when (this) {
    PromoStateFilter.All -> true
    PromoStateFilter.Active -> state == PromoCodeState.Active
    PromoStateFilter.Disabled -> state == PromoCodeState.Disabled
    PromoStateFilter.Expired -> state == PromoCodeState.Expired
    PromoStateFilter.Exhausted -> state == PromoCodeState.Exhausted
}

/** Case insensitive search on the code text or the referrer email. A blank query matches everything. */
fun matchesPromoQuery(code: String, referrerEmail: String?, query: String): Boolean {
    val q = query.trim().lowercase()
    if (q.isEmpty()) return true
    return code.lowercase().contains(q) || referrerEmail.orEmpty().lowercase().contains(q)
}

fun promoStateCounts(states: List<PromoCodeState>): Map<PromoStateFilter, Int> =
    PromoStateFilter.entries.associateWith { filter -> states.count { filter.accepts(it) } }

// ─── Amounts ─────────────────────────────────────────────────────────────────

/** Decimals of the minor unit of a currency (ISO 4217); 2 by default. */
fun currencyDecimals(currency: String): Int = when (currency.trim().uppercase()) {
    "BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW", "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF",
    "XPF" -> 0
    "BHD", "JOD", "KWD", "OMR", "TND" -> 3
    else -> 2
}

fun currencySymbol(currency: String): String = when (currency.trim().uppercase()) {
    "EUR" -> "€"
    "USD" -> "$"
    "GBP" -> "£"
    "BRL" -> "R$"
    "JPY" -> "¥"
    else -> currency.trim().uppercase()
}

private const val NarrowNoBreakSpace = '\u202F'
private const val NoBreakSpace = '\u00A0'

/**
 * Formats an amount given in the minor unit of its currency (cents for EUR), for the language of the app:
 * fr "1 234,56 €", es "1.234,56 €", pt "€ 1.234,56", en "€1,234.56". Amounts of different currencies are
 * never added: one line per currency.
 */
fun formatMinorAmount(amountMinor: Long, currency: String, language: String): String {
    val decimals = currencyDecimals(currency)
    var divisor = 1L
    repeat(decimals) { divisor *= 10 }
    val negative = amountMinor < 0
    val absolute = if (negative) -amountMinor else amountMinor
    val units = absolute / divisor
    val fraction = absolute % divisor
    val lang = language.lowercase().take(2)
    val (group, decimal) = when (lang) {
        "fr" -> NarrowNoBreakSpace to ','
        "es", "pt" -> '.' to ','
        else -> ',' to '.'
    }
    val unitsText = units.toString().reversed().chunked(3).joinToString(group.toString()).reversed()
    val number = buildString {
        if (negative) append('-')
        append(unitsText)
        if (decimals > 0) {
            append(decimal)
            append(fraction.toString().padStart(decimals, '0'))
        }
    }
    val symbol = currencySymbol(currency)
    return when (lang) {
        "fr", "es" -> "$number$NoBreakSpace$symbol"
        "pt" -> "$symbol$NoBreakSpace$number"
        else -> if (symbol.length == 1) "$symbol$number" else "$symbol$NoBreakSpace$number"
    }
}

/** One line of the commissions of a code, for one currency. */
data class PromoCommissionLine(
    val currency: String,
    val invoices: Int,
    val paid: String,
    val commission: String,
)

/** Raw totals of one currency, as answered by the server (amounts in minor units). */
data class PromoCommissionTotal(
    val currency: String,
    val invoices: Int,
    val paidAmount: Long,
    val commissionAmount: Long,
)

/**
 * Commission lines: one per currency (same currency merged, case insensitive), sorted by currency, each amount
 * formatted in its own currency. Currencies without any invoice are left out.
 */
fun promoCommissionLines(totals: List<PromoCommissionTotal>, language: String): List<PromoCommissionLine> =
    totals
        .groupBy { it.currency.trim().uppercase() }
        .map { (currency, items) ->
            PromoCommissionTotal(
                currency = currency,
                invoices = items.sumOf { it.invoices },
                paidAmount = items.sumOf { it.paidAmount },
                commissionAmount = items.sumOf { it.commissionAmount },
            )
        }
        .filter { it.invoices > 0 || it.paidAmount != 0L || it.commissionAmount != 0L }
        .sortedBy { it.currency }
        .map {
            PromoCommissionLine(
                currency = it.currency,
                invoices = it.invoices,
                paid = formatMinorAmount(it.paidAmount, it.currency, language),
                commission = formatMinorAmount(it.commissionAmount, it.currency, language),
            )
        }

// ─── End date ────────────────────────────────────────────────────────────────

/** Days since 1970-01-01 of a civil date (proleptic Gregorian calendar). */
internal fun daysFromCivil(year: Int, month: Int, day: Int): Long {
    val y = if (month <= 2) year - 1 else year
    val era = (if (y >= 0) y else y - 399) / 400
    val yoe = y - era * 400
    val mp = (month + 9) % 12
    val doy = (153 * mp + 2) / 5 + day - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    return era * 146_097L + doe - 719_468L
}

internal fun civilFromDays(days: Long): Triple<Int, Int, Int> {
    val z = days + 719_468L
    val era = (if (z >= 0) z else z - 146_096L) / 146_097L
    val doe = z - era * 146_097L
    val yoe = (doe - doe / 1460 + doe / 36_524 - doe / 146_096) / 365
    val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
    val mp = (5 * doy + 2) / 153
    val d = (doy - (153 * mp + 2) / 5 + 1).toInt()
    val m = (if (mp < 10) mp + 3 else mp - 9).toInt()
    val y = (yoe + era * 400 + if (m <= 2) 1 else 0).toInt()
    return Triple(y, m, d)
}

private fun isValidCivil(year: Int, month: Int, day: Int): Boolean {
    if (year !in 1970..9999 || month !in 1..12 || day < 1) return false
    return civilFromDays(daysFromCivil(year, month, day)) == Triple(year, month, day)
}

/**
 * End of the typed day (23:59:59, the day is included) in UTC seconds, for the local [offsetSeconds] of the
 * device. Accepts "dd/mm/yyyy" (also with - or .) and "yyyy-mm-dd". Null for a blank or invalid text.
 */
fun parsePromoEndDate(text: String, offsetSeconds: Long): Long? {
    val t = text.trim()
    if (t.isEmpty()) return null
    val parts = t.split('/', '-', '.').map { it.trim() }
    if (parts.size != 3 || parts.any { it.isEmpty() || !it.all(Char::isDigit) }) return null
    val (year, month, day) = if (parts[0].length == 4) {
        Triple(parts[0].toInt(), parts[1].toInt(), parts[2].toInt())
    } else {
        Triple(parts[2].toInt(), parts[1].toInt(), parts[0].toInt())
    }
    if (!isValidCivil(year, month, day)) return null
    return (daysFromCivil(year, month, day) + 1) * SECONDS_PER_DAY - 1 - offsetSeconds
}

/** "dd/mm/yyyy" of an end date (UTC seconds) in the local [offsetSeconds] of the device. */
fun formatPromoEndDate(secondsUtc: Long, offsetSeconds: Long): String {
    val local = secondsUtc + offsetSeconds
    val days = if (local >= 0) local / SECONDS_PER_DAY else (local - SECONDS_PER_DAY + 1) / SECONDS_PER_DAY
    val (y, m, d) = civilFromDays(days)
    return "${d.toString().padStart(2, '0')}/${m.toString().padStart(2, '0')}/$y"
}

// ─── Admin form ──────────────────────────────────────────────────────────────

/** Benefit selector of the form: % only, free months only, or both (free months then %). */
enum class PromoBenefitKind { Percent, FreeMonths, Both }

fun benefitKindOf(percentOff: Int?, freeMonths: Int?): PromoBenefitKind = when {
    freeMonths != null && percentOff != null -> PromoBenefitKind.Both
    freeMonths != null -> PromoBenefitKind.FreeMonths
    else -> PromoBenefitKind.Percent
}

/** Fields of the form, as typed. [uuid] null = creation. The original values are those read from the GET. */
data class PromoCodeForm(
    val uuid: String? = null,
    val app: String = "StrikeRadar",
    val code: String = "",
    val benefitKind: PromoBenefitKind = PromoBenefitKind.Percent,
    val percentOff: String = "",
    val freeMonths: String = "",
    val maxRedemptions: String = "",
    val endDate: String = "",
    val referral: Boolean = false,
    val referrerEmail: String = "",
    val commissionPercent: String = "",
    val active: Boolean = true,
    // Referrer as read from the GET (see [referrerEmailToSend]): null email = nothing to show
    val originalHasReferrer: Boolean = false,
    val originalReferrerEmail: String? = null,
    val originalCode: String? = null,
    val originalValidUntilSecondsUtc: Long? = null,
    val redemptions: Int = 0,
) {
    val isCreation get() = uuid == null
    val isRenamed get() = isPromoCodeRenamed(originalCode, code)
}

enum class PromoField { App, Code, PercentOff, FreeMonths, MaxRedemptions, ValidUntil, ReferrerEmail, CommissionPercent }

enum class PromoFieldError {
    CodeInvalid,
    PercentRange,
    FreeMonthsRange,
    MaxRedemptionsInvalid,
    EndDateInvalid,
    EndDateInPast,
    ReferrerEmailInvalid,
    CommissionRange,
    CommissionRequiresEmail,
    // Errors of the server (InvalidRequest conflicts), shown with the message of the server
    Server,
}

/** The values to POST, once the form is valid. Every field is present (null = none). */
data class PromoCodeSave(
    val uuid: String?,
    val app: String,
    val code: String,
    val active: Boolean,
    val percentOff: Int?,
    val freeMonths: Int?,
    val maxRedemptions: Int?,
    val validUntilSecondsUtc: Long?,
    val referrerEmail: String?,
    val commissionPercent: Int?,
)

data class PromoValidation(val errors: Map<PromoField, PromoFieldError>, val save: PromoCodeSave?) {
    val isValid get() = save != null
}

private fun String.intOrNull(): Int? = trim().takeIf { it.isNotEmpty() && it.all(Char::isDigit) && it.length <= 9 }
    ?.toInt()

/**
 * Checks the form like the server (spec of 2026-11-02) and builds the complete body:
 * - code: 3 to 32 letters, digits, - or _, saved in upper case;
 * - % 1 to 100 and / or free months 1 to 24, at least one (given by the selected benefit kind);
 * - max redemptions: integer ≥ 1, empty = unlimited;
 * - end date in the future; an end already saved (even passed) can be kept unchanged;
 * - commission 1 to 100, only with the email of the referrer.
 */
@Suppress("CyclomaticComplexMethod", "LongMethod")
fun validatePromoCodeForm(form: PromoCodeForm, nowSeconds: Long, offsetSeconds: Long): PromoValidation {
    val errors = linkedMapOf<PromoField, PromoFieldError>()
    val code = normalizePromoCode(form.code)
    if (!PromoCodeRegex.matches(code)) errors[PromoField.Code] = PromoFieldError.CodeInvalid

    val wantsPercent = form.benefitKind != PromoBenefitKind.FreeMonths
    val wantsMonths = form.benefitKind != PromoBenefitKind.Percent
    val percent = form.percentOff.intOrNull()
    val months = form.freeMonths.intOrNull()
    if (wantsPercent && (percent == null || percent !in PROMO_PERCENT_MIN..PROMO_PERCENT_MAX)) {
        errors[PromoField.PercentOff] = PromoFieldError.PercentRange
    }
    if (wantsMonths && (months == null || months !in PROMO_FREE_MONTHS_MIN..PROMO_FREE_MONTHS_MAX)) {
        errors[PromoField.FreeMonths] = PromoFieldError.FreeMonthsRange
    }

    val maxText = form.maxRedemptions.trim()
    val max = maxText.intOrNull()
    if (maxText.isNotEmpty() && (max == null || max < 1)) {
        errors[PromoField.MaxRedemptions] = PromoFieldError.MaxRedemptionsInvalid
    }

    var validUntil: Long? = null
    if (form.endDate.isNotBlank()) {
        val parsed = parsePromoEndDate(form.endDate, offsetSeconds)
        val original = form.originalValidUntilSecondsUtc
        val keepsOriginal = original != null && form.endDate.trim() == formatPromoEndDate(original, offsetSeconds)
        when {
            keepsOriginal -> validUntil = original
            parsed == null -> errors[PromoField.ValidUntil] = PromoFieldError.EndDateInvalid
            parsed <= nowSeconds -> errors[PromoField.ValidUntil] = PromoFieldError.EndDateInPast
            else -> validUntil = parsed
        }
    }

    var email: String? = null
    var commission: Int? = null
    var keepsEmaillessReferrer = false
    if (form.referral) {
        val typedEmail = form.referrerEmail.trim()
        val commissionText = form.commissionPercent.trim()
        if (typedEmail.isEmpty() && isEmaillessReferrer(form.originalHasReferrer, form.originalReferrerEmail)) {
            // Referrer without email, field untouched (nothing to show): kept as is, the email goes out as null
            keepsEmaillessReferrer = true
        } else if (typedEmail.isEmpty()) {
            if (commissionText.isNotEmpty()) {
                errors[PromoField.ReferrerEmail] = PromoFieldError.CommissionRequiresEmail
            } else {
                errors[PromoField.ReferrerEmail] = PromoFieldError.ReferrerEmailInvalid
            }
        } else if (!EmailRegex.matches(typedEmail)) {
            errors[PromoField.ReferrerEmail] = PromoFieldError.ReferrerEmailInvalid
        } else {
            email = typedEmail
        }
        if (commissionText.isNotEmpty()) {
            val c = commissionText.intOrNull()
            if (c == null || c !in PROMO_COMMISSION_MIN..PROMO_COMMISSION_MAX) {
                errors[PromoField.CommissionPercent] = PromoFieldError.CommissionRange
            } else {
                commission = c
            }
        }
    }

    if (errors.isNotEmpty()) return PromoValidation(errors, null)
    return PromoValidation(
        errors = emptyMap(),
        save = PromoCodeSave(
            uuid = form.uuid,
            app = form.app,
            code = code,
            active = form.active,
            percentOff = if (wantsPercent) percent else null,
            freeMonths = if (wantsMonths) months else null,
            maxRedemptions = max,
            validUntilSecondsUtc = validUntil,
            referrerEmail = referrerEmailToSend(email, form.originalReferrerEmail),
            commissionPercent = if (email != null || keepsEmaillessReferrer) commission else null,
        )
    )
}

/** Field of an `InvalidRequest` conflict (`conflicts[].field` of the server), null for an unknown path. */
fun promoFieldOfConflict(field: String?): PromoField? = when (field?.trim()) {
    "app" -> PromoField.App
    "code" -> PromoField.Code
    "percentOff" -> PromoField.PercentOff
    "freeMonths" -> PromoField.FreeMonths
    "maxRedemptions" -> PromoField.MaxRedemptions
    "validUntilSecondsUtc" -> PromoField.ValidUntil
    "referrerEmail" -> PromoField.ReferrerEmail
    "commissionPercent" -> PromoField.CommissionPercent
    else -> null
}

// ─── User field: check of a code ─────────────────────────────────────────────

/** Why a code is refused at the check (GET /api/checkout/promotion-codes/{code}) or at the checkout. */
enum class PromoCheckFailure { NotFound, Expired, AlreadyUsed, NotApplicable, Unavailable }

/** Failure of a promotion code error code of the API, null for any other code (handled by the caller). */
fun promoFailureForErrorCode(code: String?): PromoCheckFailure? = when (code?.trim()) {
    "PromotionCodeNotFound" -> PromoCheckFailure.NotFound
    "PromotionCodeExpired" -> PromoCheckFailure.Expired
    "PromotionCodeAlreadyUsed" -> PromoCheckFailure.AlreadyUsed
    "PromotionCodeNotApplicable" -> PromoCheckFailure.NotApplicable
    else -> null
}

private val PromoErrorCodeRegex = Regex("""\\?"code\\?"\s*:\s*\\?"([A-Za-z]{1,64})""")

/** `code` of an ApiError body, read leniently (null for an empty or unexpected body). */
fun promoErrorCodeOf(body: String?): String? {
    if (body.isNullOrBlank()) return null
    return PromoErrorCodeRegex.find(body)?.groupValues?.get(1)
}

/**
 * Failure of the check route: the `code` of the body wins; without it, 404 = unknown code, anything else
 * (401, 429, 5xx, network) = the check is not possible right now.
 */
fun classifyPromoCheckFailure(httpStatus: Int?, body: String?): PromoCheckFailure =
    promoFailureForErrorCode(promoErrorCodeOf(body))
        ?: if (httpStatus == 404) PromoCheckFailure.NotFound else PromoCheckFailure.Unavailable

/**
 * A code only applies to a new subscription: the field is shown to a logged in account, without running
 * subscription (null = unknown, the server decides), and never for a demo (refused by the server).
 */
fun isPromoFieldVisible(loggedIn: Boolean, subscriptionRunning: Boolean?, isDemoRequest: Boolean): Boolean =
    loggedIn && subscriptionRunning != true && !isDemoRequest

/** "Apply" is enabled for a non blank code while no check is running. */
fun canApplyPromoCode(input: String, checking: Boolean): Boolean = !checking && normalizePromoCode(input).isNotEmpty()

/**
 * A referrer exists but its email is unknown: the API returns the referrer without email. The POST rebuilds the
 * referrer from `referrerEmail`, so sending the code back would erase the referral. Criterion: no usable email AND
 * (a referrer object is present OR a commission above 0 is set). Disable / enable / edit are blocked in that case.
 */
fun isReferrerWithoutEmail(hasReferrer: Boolean, referrerEmail: String?, commissionPercent: Int?): Boolean =
    referrerEmail.isNullOrBlank() && (hasReferrer || (commissionPercent ?: 0) > 0)

/** A referrer exists on the code but the server gave no email for it (nothing to show in the field). */
fun isEmaillessReferrer(hasReferrer: Boolean, originalEmail: String?): Boolean =
    hasReferrer && originalEmail.isNullOrBlank()

/**
 * `referrerEmail` of the PUT/PATCH body. The server removes the referrer on "" and keeps it on null:
 * - a valid [typedEmail] -> that email;
 * - no email now and the code had one ([originalEmail] not blank): the admin removed it -> "" (removes the referrer);
 * - no email now and none originally (nothing was shown): null (the referrer, if any, stays).
 */
fun referrerEmailToSend(typedEmail: String?, originalEmail: String?): String? = when {
    !typedEmail.isNullOrBlank() -> typedEmail
    !originalEmail.isNullOrBlank() -> ""
    else -> null
}
